import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { FaPrint } from "react-icons/fa";
import "./CheckWriter.css";
import "./ViewPayroll.css";
import SuccessPopup from "./SuccessPopup";

function TabSvgDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    >
      <defs>
        <filter id="tabShadow">
          <feComponentTransfer in="SourceGraphic">
            <feFuncR type="discrete" tableValues="0" />
            <feFuncG type="discrete" tableValues="0" />
            <feFuncB type="discrete" tableValues="0" />
          </feComponentTransfer>
          <feGaussianBlur stdDeviation="1" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.2" />
          </feComponentTransfer>
          <feOffset dx="5" dy="1" result="shadow" />
          <feComposite in="SourceGraphic" />
        </filter>

        <linearGradient id="tab-inactive-bg" x1="0%" y1="0%" x2="0%" y2="65%">
          <stop offset="0%" stopColor="#E5ECF6" />
          <stop offset="100%" stopColor="#D7E0ED" />
        </linearGradient>

        <linearGradient id="tab-active-bg" x1="0%" y1="0%" x2="0%" y2="65%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#FAFBFC" />
        </linearGradient>

        <symbol id="tab-shape" viewBox="0 0 122 40">
          <path d="M116.486,29.036c-23.582-8-14.821-29-42.018-29h-62.4C5.441,0.036,0,5.376,0,12.003v28.033h122v-11H116.486z" />
        </symbol>
      </defs>
    </svg>
  );
}

function SvgTabs({ tabs, activeIndex, onChange }) {
  return (
    <nav
      className="svg-tabs"
      role="navigation"
      aria-label="Cheque sections"
    >
      <ul className="svg-tablist">
        {tabs.map((label, i) => {
          const active = i === activeIndex;
          return (
            <li key={label} className={`svg-tab ${active ? "is-active" : ""}`}>
              <button
                type="button"
                className="svg-tab-link"
                onClick={() => onChange(i)}
                aria-selected={active}
                aria-controls={`panel-${i}`}
                role="tab"
              >
                <span className="svg-tab-label">{label}</span>
                <svg className="svg-tab-bg" viewBox="0 0 122 40" aria-hidden="true">
                  <use xlinkHref="#tab-shape" />
                </svg>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default function CheckWriterPage() {
  const { id: businessId } = useParams();

  const TAB_KEYS = ["list", "new"];
  const TAB_LABELS = ["Check List", "New Check"];
  const [activeTab, setActiveTab] = useState("list");
  const activeTabIndex = Math.max(0, TAB_KEYS.indexOf(activeTab));
  const setActiveByIndex = (i) => setActiveTab(TAB_KEYS[i] ?? "list");

  const [showBankAddedPopup, setShowBankAddedPopup] = useState(false);
  const [checks, setChecks] = useState([]);
  const [selectedChecks, setSelectedChecks] = useState([]);

  const [formData, setFormData] = useState({
    payableTo: "",
    date: "",
    amount: "",
    memo: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchChecks = useCallback(async () => {
    try {
      const response = await api.get(`/checks/${businessId}`);
      setChecks(response.data.checks || []);
    } catch (error) {
      console.error("Error fetching checks:", error);
    }
  }, [businessId]);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  const toggleCheckSelection = (checkNo) => {
    setSelectedChecks((prev) =>
      prev.includes(checkNo) ? prev.filter((n) => n !== checkNo) : [...prev, checkNo]
    );
  };

  const handlePrint = async () => {
    try {
      if (selectedChecks.length === 0) {
        alert("Please select at least one cheque to print.");
        return;
      }

      const response = await api.post(
        `/checks/print`,
        { ids: selectedChecks },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "selected_cheques.pdf";
      link.click();
      setSelectedChecks([]);
    } catch (err) {
      console.error("Error printing checks:", err);
      alert("Failed to print selected cheques. Please try again.");
    }
  };

  const handleChange = (e) => {
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.payableTo) errs.payableTo = "Required";
    if (!formData.date) errs.date = "Required";
    if (!formData.amount || isNaN(formData.amount) || Number(formData.amount) <= 0) {
      errs.amount = "Enter a valid amount";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await api.post("/write-check", {
        businessId,
        payableTo: formData.payableTo,
        date: formData.date,
        amount: formData.amount,
        memo: formData.memo || null,
      });

      setFormData({
        payableTo: "",
        date: "",
        amount: "",
        memo: "",
      });

      setErrors({});
      await fetchChecks();
      setShowBankAddedPopup(true);
      setActiveTab("list");
    } catch (error) {
      console.error("Error submitting cheque:", error);
      alert("Error submitting cheque.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pwc-checkwriter-page view-payroll-page">
      <TabSvgDefs />

      <h1 className="pw-page-title">Check Writer</h1>

      <SvgTabs
        tabs={TAB_LABELS}
        activeIndex={activeTabIndex}
        onChange={setActiveByIndex}
      />

      <div className="svg-panels">
        <div
          id="panel-0"
          className={`pw-card tab-panel pwc-checkwriter-panel pwc-checkwriter-panel-list ${
            activeTab === "list" ? "is-active" : ""
          }`}
        >
          <div className="pwc-checkwriter-table-shell">
            <div className="pwc-checkwriter-table-head">
              <table className="pwc-checkwriter-table pwc-checkwriter-table-headonly">
                <colgroup>
                  <col className="pwc-col-checkno" />
                  <col className="pwc-col-payee" />
                  <col className="pwc-col-date" />
                  <col className="pwc-col-amount" />
                  <col className="pwc-col-memo" />
                  <col className="pwc-col-action" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Check No</th>
                    <th>Payee Name</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th className="pwc-checkwriter-memo-col">Memo</th>
                    <th className="pwc-checkwriter-print-col">
                      <div className="pwc-checkwriter-print-wrap">
                        <FaPrint
                          className="pwc-checkwriter-print-icon"
                          onClick={handlePrint}
                          title="Print Selected"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
              </table>
            </div>

            <div className="pwc-checkwriter-table-scroll">
              <table className="pwc-checkwriter-table pwc-checkwriter-table-body">
                <colgroup>
                  <col className="pwc-col-checkno" />
                  <col className="pwc-col-payee" />
                  <col className="pwc-col-date" />
                  <col className="pwc-col-amount" />
                  <col className="pwc-col-memo" />
                  <col className="pwc-col-action" />
                </colgroup>
                <tbody>
                  {checks.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="pwc-checkwriter-empty-cell">
                        No checks found.
                      </td>
                    </tr>
                  ) : (
                    checks.map((check) => {
                      const isSelected = selectedChecks.includes(check.check_no);

                      return (
                        <tr
                          key={check.check_no}
                          onClick={() => toggleCheckSelection(check.check_no)}
                          className={isSelected ? "pwc-checkwriter-row-selected" : ""}
                        >
                          <td>{check.check_no}</td>
                          <td>{check.payable_to}</td>
                          <td>{new Date(check.check_date).toLocaleDateString()}</td>
                          <td>
                            ${Number(check.amount).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="pwc-checkwriter-memo-col">
                            {check.memo || "N/A"}
                          </td>
                          <td className="pwc-checkwriter-select-cell">
                            {isSelected ? "Selected" : ""}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div
          id="panel-1"
          className={`pw-card tab-panel pwc-checkwriter-panel pwc-checkwriter-panel-new ${
            activeTab === "new" ? "is-active" : ""
          }`}
        >
          {activeTab === "new" && (
            <div className="pwc-checkwriter-form-shell">
              <div className="pwc-checkwriter-form-header">
                <h2>Create New Check</h2>
                <p>Enter the payee details and amount to create a new check.</p>
              </div>

              <form onSubmit={handleSubmit} className="pwc-checkwriter-form">
                <div className="pwc-checkwriter-grid">
                  <div className="pwc-checkwriter-field">
                    <label>Payable To</label>
                    <input
                      type="text"
                      name="payableTo"
                      value={formData.payableTo}
                      onChange={handleChange}
                      placeholder="Enter payee name"
                    />
                    {errors.payableTo && (
                      <p className="pwc-checkwriter-error-text">{errors.payableTo}</p>
                    )}
                  </div>

                  <div className="pwc-checkwriter-field">
                    <label>Date</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                    />
                    {errors.date && (
                      <p className="pwc-checkwriter-error-text">{errors.date}</p>
                    )}
                  </div>
                </div>

                <div className="pwc-checkwriter-grid">
                  <div className="pwc-checkwriter-field">
                    <label>Amount ($)</label>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="0.00"
                    />
                    {errors.amount && (
                      <p className="pwc-checkwriter-error-text">{errors.amount}</p>
                    )}
                  </div>

                  <div className="pwc-checkwriter-field">
                    <label>Memo</label>
                    <input
                      type="text"
                      name="memo"
                      value={formData.memo}
                      onChange={handleChange}
                      placeholder="Optional memo"
                    />
                  </div>
                </div>

                <div className="pwc-checkwriter-form-actions">
                  <button
                    type="submit"
                    className="pwc-checkwriter-submit-btn"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit Check"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {showBankAddedPopup && (
        <SuccessPopup
          title="Check Added!"
          message="Your check has been successfully created."
          onClose={() => setShowBankAddedPopup(false)}
          buttonLabel="Done"
        />
      )}
    </div>
  );
}

// import React, { useState, useEffect, useCallback } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";
// import { FaPrint } from "react-icons/fa";
// import "./CheckWriter.css";           // your existing styles
// import "./ViewPayroll.css";           // for svg tabs + pw-card/tab-panel styles
// import SuccessPopup from "./SuccessPopup";

// /* ===============================
//    SVG TAB DEFS + COMPONENT
//    (same as ViewPayrollPage)
//    =============================== */
// function TabSvgDefs() {
//   return (
//     <svg width="0" height="0" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
//       <defs>
//         <filter id="tabShadow">
//           <feComponentTransfer in="SourceGraphic">
//             <feFuncR type="discrete" tableValues="0" />
//             <feFuncG type="discrete" tableValues="0" />
//             <feFuncB type="discrete" tableValues="0" />
//           </feComponentTransfer>
//           <feGaussianBlur stdDeviation="1" />
//           <feComponentTransfer>
//             <feFuncA type="linear" slope="0.2" />
//           </feComponentTransfer>
//           <feOffset dx="5" dy="1" result="shadow" />
//           <feComposite in="SourceGraphic" />
//         </filter>

//         <linearGradient id="tab-inactive-bg" x1="0%" y1="0%" x2="0%" y2="65%">
//           <stop offset="0%" stopColor="#E5ECF6" />
//           <stop offset="100%" stopColor="#D7E0ED" />
//         </linearGradient>

//         <linearGradient id="tab-active-bg" x1="0%" y1="0%" x2="0%" y2="65%">
//           <stop offset="0%" stopColor="#FFFFFF" />
//           <stop offset="100%" stopColor="#FAFBFC" />
//         </linearGradient>

//         <symbol id="tab-shape" viewBox="0 0 122 40">
//           <path d="M116.486,29.036c-23.582-8-14.821-29-42.018-29h-62.4C5.441,0.036,0,5.376,0,12.003v28.033h122v-11H116.486z" />
//         </symbol>
//       </defs>
//     </svg>
//   );
// }

// function SvgTabs({ tabs, activeIndex, onChange }) {
//   return (
//     <nav className="svg-tabs" role="navigation" aria-label="Cheque sections">
//       <ul className="svg-tablist">
//         {tabs.map((label, i) => {
//           const active = i === activeIndex;
//           return (
//             <li key={label} className={`svg-tab ${active ? "is-active" : ""}`}>
//               <button
//                 type="button"
//                 className="svg-tab-link"
//                 onClick={() => onChange(i)}
//                 aria-selected={active}
//                 aria-controls={`panel-${i}`}
//                 role="tab"
//               >
//                 <span className="svg-tab-label">{label}</span>
//                 <svg className="svg-tab-bg" viewBox="0 0 122 40" aria-hidden="true">
//                   <use xlinkHref="#tab-shape" />
//                 </svg>
//               </button>
//             </li>
//           );
//         })}
//       </ul>
//     </nav>
//   );
// }

// /* ===============================
//    PAGE
//    =============================== */
// export default function CheckWriterPage() {
//   const { id: businessId } = useParams();

//   // Tabs
//   const TAB_KEYS = ["list", "new"];
//   const TAB_LABELS = ["Check List", "New Check"];
//   const [activeTab, setActiveTab] = useState("list");
//   const activeTabIndex = Math.max(0, TAB_KEYS.indexOf(activeTab));
//   const setActiveByIndex = (i) => setActiveTab(TAB_KEYS[i] ?? "list");


//   const [showBankAddedPopup, setShowBankAddedPopup] = useState(false);

//   // Data
//   const [checks, setChecks] = useState([]);
//   const [selectedChecks, setSelectedChecks] = useState([]);

//   // Form
//   const [formData, setFormData] = useState({
//     payableTo: "",
//     date: "",
//     amount: "",
//     memo: "",
//   });
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);

//   // Fetch checks
//   const fetchChecks = useCallback(async () => {
//     try {
//       const response = await api.get(`/checks/${businessId}`);
//       setChecks(response.data.checks || []);
//     } catch (error) {
//       console.error("Error fetching checks:", error);
//     }
//   }, [businessId]);

//   useEffect(() => {
//     fetchChecks();
//   }, [fetchChecks]);

//   // Selection
//   const toggleCheckSelection = (checkNo) => {
//     setSelectedChecks((prev) =>
//       prev.includes(checkNo) ? prev.filter((n) => n !== checkNo) : [...prev, checkNo]
//     );
//   };

//   // Print selected
//   const handlePrint = async () => {
//     try {
//       if (selectedChecks.length === 0) {
//         alert("Please select at least one cheque to print.");
//         return;
//       }
//       const response = await api.post(
//         `/checks/print`,
//         { ids: selectedChecks },
//         { responseType: "blob" }
//       );
//       const blob = new Blob([response.data], { type: "application/pdf" });
//       const link = document.createElement("a");
//       link.href = URL.createObjectURL(blob);
//       link.download = "selected_cheques.pdf";
//       link.click();
//       setSelectedChecks([]);
//     } catch (err) {
//       console.error("Error printing checks:", err);
//       alert("Failed to print selected cheques. Please try again.");
//     }
//   };

//   // Form helpers
//   const handleChange = (e) => {
//     setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
//   };

//   const validateForm = () => {
//     const errs = {};
//     if (!formData.payableTo) errs.payableTo = "Required";
//     if (!formData.date) errs.date = "Required";
//     if (!formData.amount || isNaN(formData.amount) || Number(formData.amount) <= 0) {
//       errs.amount = "Enter a valid amount";
//     }
//     setErrors(errs);
//     return Object.keys(errs).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateForm()) return;
//     setLoading(true);
//     try {
//       await api.post("/write-check", {
//         businessId,
//         payableTo: formData.payableTo,
//         date: formData.date,
//         amount: formData.amount,
//         memo: formData.memo || null,
//       });
//       setFormData({ payableTo: "", date: "", amount: "", memo: "" });
//       await fetchChecks();
//       setShowBankAddedPopup(true);
//       setActiveTab("list"); // go back to list after creating
//     } catch (error) {
//       console.error("Error submitting cheque:", error);
//       alert("Error submitting cheque.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="view-payroll-page"> {/* reuses tab/panel styles */}
//       <TabSvgDefs />

//       <h1 className="pw-page-title">Check Writer</h1>

//       {/* SVG Tabs */}
//       <SvgTabs tabs={TAB_LABELS} activeIndex={activeTabIndex} onChange={setActiveByIndex} />

//       <div className="svg-panels">
//         {/* ===== List Tab ===== */}
//         <div id="panel-0" className={`pw-card tab-panel ${activeTab === "list" ? "is-active" : ""}`}>
  
//           <div className="check-table-shell">
//   <div className="check-table-head">
//     <table className="check-table check-table--head">
//       <thead>
//   <tr>
//     <th>Check No</th>
//     <th>Payee Name</th>
//     <th>Date</th>
//     <th>Amount</th>
//     <th className="memo-none">Memo</th>
//     <th className="print-header">
//       <div className="print-header-wrap">
//         <FaPrint
//           className="print-icon-view"
//           onClick={handlePrint}
//           title="Print Selected"
//         />
//       </div>
//     </th>
//   </tr>
// </thead>
//     </table>
//   </div>

//   <div className="check-table-body-scroll">
//     <table className="check-table check-table--body">
//       <tbody>
//         {checks.length === 0 ? (
//           <tr>
//             <td colSpan="5" className="text-center">No checks found.</td>
//           </tr>
//         ) : (
//           checks.map((check) => {
//             const isSelected = selectedChecks.includes(check.check_no);
//             return (
//               <tr
//                 key={check.check_no}
//                 onClick={() => toggleCheckSelection(check.check_no)}
//                 style={{
//                   backgroundColor: isSelected ? "var(--bg)" : "var(--surface-2)",
//                   cursor: "pointer",
//                   borderRadius: "18px",
//                   marginBottom: "10px",
//                   boxShadow: "var(--shadow)",
//                 }}
//               >
//                 <td>{check.check_no}</td>
//                 <td>{check.payable_to}</td>
//                 <td>{new Date(check.check_date).toLocaleDateString()}</td>
//                 <td>
//                   ${Number(check.amount).toLocaleString(undefined, {
//                     minimumFractionDigits: 2,
//                     maximumFractionDigits: 2
//                   })}
//                 </td>
//                 <td className="memo-none">{check.memo || "N/A"}</td>
//               </tr>
//             );
//           })
//         )}
//       </tbody>
//     </table>
//   </div>
// </div>
//         </div>

//         {/* ===== New Cheque Tab ===== */}
//         <div id="panel-1" className={`pw-card tab-panel ${activeTab === "new" ? "is-active" : ""}`}>
//           {activeTab === "new" && (
//             <form onSubmit={handleSubmit} className="form-check-writer" style={{ maxWidth: 420, margin: "auto" }}>
//               <div>
//                 <label>Payable To</label>
//                 <input
//                   type="text"
//                   name="payableTo"
//                   value={formData.payableTo}
//                   onChange={handleChange}
//                   className="mt-1"
//                 />
//                 {errors.payableTo && <p className="text-red-500 text-sm">{errors.payableTo}</p>}
//               </div>

//               <div>
//                 <label>Date</label>
//                 <input
//                   type="date"
//                   name="date"
//                   value={formData.date}
//                   onChange={handleChange}
//                 />
//                 {errors.date && <p className="text-red-500 text-sm">{errors.date}</p>}
//               </div>

//               <div>
//                 <label>Amount ($)</label>
//                 <input
//                   type="number"
//                   name="amount"
//                   value={formData.amount}
//                   onChange={handleChange}
//                   className="mt-1"
//                 />
//                 {errors.amount && <p className="text-red-500 text-sm">{errors.amount}</p>}
//               </div>

//               <div>
//                 <label>Memo</label>
//                 <input
//                   type="text"
//                   name="memo"
//                   value={formData.memo}
//                   onChange={handleChange}
//                   className="mt-1"
//                 />
//               </div>

//               <button type="submit" className="button-check-writer" disabled={loading}>
//                 {loading ? "Submitting..." : "Submit"}
//               </button>
//             </form>
//           )}
//         </div>
//       </div>
//       {showBankAddedPopup && (
//         <SuccessPopup
//           title="Check Added!"
//           message="Your check has been successfully created."
//           onClose={() => setShowBankAddedPopup(false)}
//           buttonLabel="Done"
//         />
//       )}
//     </div>
    
//   );
// }





// import { useState, useEffect, useCallback } from "react";
// import { useParams } from "react-router-dom";
// import { Card, CardBody } from "reactstrap";
// import { FaPrint } from "react-icons/fa";
// import api from "../services/api";
// import "./CheckWriter.css";


// export default function CheckWriter() {
//     const { id: businessId } = useParams();
//     const [formData, setFormData] = useState({
//         payableTo: "",
//         date: "",
//         amount: "",
//         memo: "",
//     });
//     const [checks, setChecks] = useState([]);
//     const [selectedChecks, setSelectedChecks] = useState([]);
//     const [errors, setErrors] = useState({});
//     const [loading, setLoading] = useState(false);

//     // ✅ Fetch Checks
//     const fetchChecks = useCallback(async () => {
//         try {
//             const response = await api.get(`/checks/${businessId}`);
//             setChecks(response.data.checks);
//         } catch (error) {
//             console.error("Error fetching checks:", error);
//         }
//     }, [businessId]);

//     useEffect(() => {
//         fetchChecks();
//     }, [fetchChecks]);

//     const handleChange = (e) => {
//         setFormData({ ...formData, [e.target.name]: e.target.value });
//     };

//     const validateForm = () => {
//         let newErrors = {};
//         if (!formData.payableTo) newErrors.payableTo = "Required";
//         if (!formData.date) newErrors.date = "Required";
//         if (!formData.amount || isNaN(formData.amount) || formData.amount <= 0)
//             newErrors.amount = "Enter a valid amount";
//         setErrors(newErrors);
//         return Object.keys(newErrors).length === 0;
//     };

//     // ✅ Handle Form Submission
//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         if (!validateForm()) return;

//         setLoading(true);
//         try {
//             await api.post("/write-check", {
//                 businessId,
//                 payableTo: formData.payableTo,
//                 date: formData.date,
//                 amount: formData.amount,
//                 memo: formData.memo || null,
//             });
//             setFormData({ payableTo: "", date: "", amount: "", memo: "" });
//             fetchChecks(); // Refresh list
//         } catch (error) {
//             console.error("Error submitting check:", error);
//             alert("Error submitting check.");
//         } finally {
//             setLoading(false);
//         }
//     };

//     // ✅ Handle Checkbox Selection
//     const toggleCheckSelection = (checkNo) => {
//         setSelectedChecks((prev) =>
//             prev.includes(checkNo)
//                 ? prev.filter((num) => num !== checkNo)
//                 : [...prev, checkNo]
//         );
//     };

//     // ✅ Handle Print (Request PDF from Backend)
//     const handlePrint = async () => {
//         try {
//             if (selectedChecks.length === 0) {
//                 alert("Please select at least one check to print.");
//                 return;
//             }


//             const response = await api.post(
//                 `/checks/print`,
//                 { ids: selectedChecks },
//                 { responseType: "blob" }
//             );

//             const blob = new Blob([response.data], { type: "application/pdf" });
//             const link = document.createElement("a");
//             link.href = URL.createObjectURL(blob);
//             link.download = "selected_checks.pdf";
//             link.click();

//             setSelectedChecks([]); // ✅ Reset selection after printing
//         } catch (err) {
//             console.error("Error printing checks:", err);
//             alert("Failed to print selected checks. Please try again.");
//         }
//     };

//     return (
//         <div>
//         <header className="check-writer-header">
//             <h3>Check Writer</h3>
//         </header>
//         <div className="check-writer">
         
//             {/* Left: Form */}
//             <div className="form-container">
//                 <Card className="card-check-writer">
//                     <CardBody>
                        
//                         <form onSubmit={handleSubmit} className="form-check-writer">
//                             <div>
//                                 <label>Payable To</label>
//                                 <input
//                                     type="text"
//                                     name="payableTo"
//                                     value={formData.payableTo}
//                                     onChange={handleChange}
//                                     className="mt-1"
//                                 />
//                                 {errors.payableTo && <p className="text-red-500 text-sm">{errors.payableTo}</p>}
//                             </div>
    
//                             <div>
//                                 <label>Date</label>
//                                 <input
//                                     type="date"
//                                     name="date"
//                                     value={formData.date}
//                                     onChange={handleChange}
//                                 />
//                                 {errors.date && <p className="text-red-500 text-sm">{errors.date}</p>}
//                             </div>
    
//                             <div>
//                                 <label>Amount ($)</label>
//                                 <input
//                                     type="number"
//                                     name="amount"
//                                     value={formData.amount}
//                                     onChange={handleChange}
//                                     className="mt-1"
//                                 />
//                                 {errors.amount && <p className="text-red-500 text-sm">{errors.amount}</p>}
//                             </div>
    
//                             <div>
//                                 <label>Memo</label>
//                                 <input
//                                     type="text"
//                                     name="memo"
//                                     value={formData.memo}
//                                     onChange={handleChange}
//                                     className="mt-1"
//                                 />
//                             </div>
    
//                             <button type="submit" className="button-check-writer" disabled={loading}>
//                                 {loading ? "Submitting..." : "Submit"}
//                             </button>
//                         </form>
//                     </CardBody>
//                 </Card>
//             </div>
    
//             {/* Right: Table */}
//             <div className="check-table-container">
//                 <FaPrint className="print-icon-view" onClick={handlePrint} title="Print Selected" />
    
//              <table className="check-table">

//                     <thead className="thead-check">
//                         <tr>
//                             <th>Check No</th>
//                             <th>Payable To</th>
//                             <th>Date</th>
//                             <th>Amount</th>
//                             <th>Memo</th>
//                         </tr>
//                     </thead>
//                     <tbody className="tbody-check">
//                         {checks.length === 0 ? (
//                             <tr>
//                                 <td colSpan="6" className="text-center">No checks found.</td>
//                             </tr>
//                         ) : (
//                             checks.map((check) => {
//                                 const isSelected = selectedChecks.includes(check.check_no);
//                                 return (
//                                     <tr
//                                         key={check.check_no}
//                                         onClick={() => toggleCheckSelection(check.check_no)}                                        
//                                         style={{
//                                             backgroundColor: isSelected ? "var(--surface)" : "var(--surface-2)",
//                                             cursor: "pointer",
//                                         }}
//                                     >
//                                         <td>{check.check_no}</td>
//                                         <td>{check.payable_to}</td>
//                                         <td>{new Date(check.check_date).toLocaleDateString()}</td>
//                                         <td>${check.amount}</td>
//                                         <td>{check.memo || "N/A"}</td>
//                                     </tr>
//                                 );
//                             })
//                         )}
//                     </tbody>
//                 </table>
//             </div>
//         </div>
//         </div>
//     );
    
// }


